
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Topic = { id: string; name: string; parent_topic_id: string | null };

interface TopicFormProps {
  topics: Topic[];
  initialName?: string;
  initialParentId?: string | null;
  submitting?: boolean;
  onSubmit: (payload: { name: string; parent_topic_id: string | null }) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

export const TopicForm = ({
  topics,
  initialName = "",
  initialParentId = null,
  submitting = false,
  onSubmit,
  onCancel,
  submitLabel = "Save",
}: TopicFormProps) => {
  const [name, setName] = useState(initialName);
  const [parentId, setParentId] = useState<string | null>(initialParentId);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  useEffect(() => {
    setParentId(initialParentId ?? null);
  }, [initialParentId]);

  const parentChoices = useMemo(
    () => topics.filter((t) => t.name !== name), // prevent selecting itself if names collide during edit
    [topics, name]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), parent_topic_id: parentId });
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-3">
      <div className="grid gap-2">
        <label className="text-sm font-medium">Name</label>
        <Input
          placeholder="e.g., Cardiology"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium">Parent (optional)</label>
        <Select
          value={parentId ?? "none"}
          onValueChange={(v) => setParentId(v === "none" ? null : v)}
          disabled={submitting}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select parent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {parentChoices.filter(t => t.id && t.id.trim() !== '').map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
};

export default TopicForm;
